import { Box, Button, Center, Container, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OfferStatus } from '@/api/generated/models/offerStatus';
import { useSearchOffers } from '@/api/generated/offers/offers';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import {
  CardSection,
  ListPagination,
  ListTotalCard,
  ListTotalRow,
  MobileFab,
  MobileListFilters,
  PageHeading,
  RowActionsMenu,
  SearchField,
  SelectField,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_CODE_COL_WIDTH,
  TABLE_DATE_COL_WIDTH,
  TABLE_OBJECT_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableMinWidth,
  tableRowStyle
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { statusConfig } from '@/features/offers/offerDisplay';
import { formatAmount, formatDate, otherPartyName } from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { usePageSize } from '@/utils/usePageSize';
import { useUrlNullableParam, useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

const handlePreviewOffer = (offerId?: string) => {
  if (!offerId) return;
  void openBinaryInNewTab(`/api/v1/offers/${offerId}/pdf`);
};

const cellStyle = { color: 'var(--color-text-secondary)' };

const DESKTOP_COLUMNS: [string, number, ('right' | 'center' | undefined)?][] = [
  ['offers.colPartner', 200],
  ['offers.colObject', TABLE_OBJECT_COL_WIDTH],
  ['offers.colNumber', TABLE_CODE_COL_WIDTH],
  ['offers.colAmount', 120, 'right'],
  ['offers.colDate', TABLE_DATE_COL_WIDTH],
  ['offers.colValidity', TABLE_DATE_COL_WIDTH],
  ['offers.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
];

const TABLE_MIN_WIDTH = tableMinWidth(
  [...DESKTOP_COLUMNS.map(([, width]) => width), TABLE_ACTION_COL_WIDTH],
  20
);

export const OffersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [counterpartyFilter, setCounterpartyFilter] = useUrlNullableParam('counterpartyOrgId');
  const [search, setSearch] = useUrlTextParam('search');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, counterpartyFilter, pageSize]);

  const { data: connectionsData } = useListOrganisationConnections();
  const counterpartyOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );

  // refetchOnMount: 'always' — lista mora biti svježa svaki put kad se uđe na
  // ekran, ne samo kad je staleTime istekao (npr. netom poslana ponuda mora
  // odmah biti vidljiva, ne tek za 5 min).
  const { data, isLoading } = useSearchOffers(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      counterpartyOrgId: counterpartyFilter ?? undefined
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageItems = data?.content ?? [];
  // Traka UKUPNO = zbroj TRENUTNE stranice (ne cijelog filtriranog skupa).
  const totalAmount = pageItems.reduce((sum, offer) => sum + (offer.totalValue ?? 0), 0);
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const statusOptions = [
    { value: 'ALL', label: t('offers.allStatuses') },
    ...Object.values(OfferStatus).map((status) => ({
      value: status,
      label: t(statusConfig[status].labelKey)
    }))
  ];

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <Group justify="space-between" wrap="wrap" align="flex-start" visibleFrom="sm">
          <PageHeading title={t('offers.title')} subtitle={t('offers.subtitle')} />
          <Button onClick={() => navigate('/offers/new')}>{t('offers.create')}</Button>
        </Group>

        <Stack gap={12} hiddenFrom="sm">
          <PageHeading title={t('offers.title')} subtitle={t('offers.subtitle')} />
          <MobileFab onClick={() => navigate('/offers/new')}>{t('offers.create')}</MobileFab>
        </Stack>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('offers.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              },
              {
                key: 'counterparty',
                label: t('offers.filterCounterparty'),
                options: counterpartyOptions,
                value: counterpartyFilter,
                onChange: setCounterpartyFilter
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('offers.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <div style={{ width: 220 }}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </div>
          <div style={{ width: 200 }}>
            <SelectField
              hideLabel
              placeholder={t('offers.filterCounterparty')}
              data={counterpartyOptions}
              value={counterpartyFilter}
              onChange={setCounterpartyFilter}
              clearable
            />
          </div>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('offers.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                    <div
                      className="table-header-row"
                      style={{
                        ...tableRowStyle,
                        padding: '12px 20px',
                        backgroundColor: 'var(--color-bg-canvas)',
                        borderBottom: '1px solid var(--color-border-soft)'
                      }}
                    >
                      {DESKTOP_COLUMNS.map(([key, width, align]) => (
                        <Text
                          key={key}
                          fz={10}
                          fw={600}
                          w={width}
                          ta={align}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                        >
                          {t(key)}
                        </Text>
                      ))}
                      <Text
                        fz={10}
                        fw={600}
                        w={TABLE_ACTION_COL_WIDTH}
                        style={{
                          ...tableCenteredCellStyle,
                          color: 'var(--color-text-muted)',
                          letterSpacing: 0.4
                        }}
                      >
                        {t('offers.colAction')}
                      </Text>
                    </div>

                    {pageItems.map((offer, index) => {
                      const config = statusConfig[offer.status ?? OfferStatus.DRAFT];

                      return (
                        <div
                          key={offer.id}
                          style={{
                            ...tableRowStyle,
                            padding: '14px 20px',
                            borderTop:
                              index === 0 ? undefined : '1px solid var(--color-border-soft)'
                          }}
                        >
                          <Text fz={13} w={200} style={cellStyle} truncate>
                            {otherPartyName(offer, user?.orgName)}
                          </Text>
                          <Tooltip
                            label={
                              offer.objectCode || offer.objectName
                                ? `${offer.objectCode ?? ''}${offer.objectCode && offer.objectName ? ' - ' : ''}${offer.objectName ?? ''}`
                                : '—'
                            }
                            openDelay={300}
                            withArrow
                            color="var(--color-bg-surface)"
                            styles={{
                              tooltip: {
                                color: 'var(--color-text-primary)',
                                border: '1px solid var(--color-border-soft)',
                                borderRadius: 8,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                              }
                            }}
                          >
                            <Text
                              fz={13}
                              w={TABLE_OBJECT_COL_WIDTH}
                              style={cellStyle}
                              truncate
                              data-own-tooltip
                            >
                              {offer.objectName ?? '—'}
                            </Text>
                          </Tooltip>
                          <Text
                            fz={13.5}
                            fw={600}
                            w={TABLE_CODE_COL_WIDTH}
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {offer.offerNumber ?? offer.referenceId}
                          </Text>
                          <Text
                            fz={13.5}
                            fw={600}
                            w={120}
                            ta="right"
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {formatAmount(offer.totalValue)}
                          </Text>
                          <Text
                            fz={12}
                            w={TABLE_DATE_COL_WIDTH}
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            {formatDate(offer.createdAt)}
                          </Text>
                          <Text
                            fz={12}
                            w={TABLE_DATE_COL_WIDTH}
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            {formatDate(offer.validUntil)}
                          </Text>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                            <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                          </div>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_ACTION_COL_WIDTH }}>
                            <RowActionsMenu
                              detailsHref={`/offers/${offer.id}`}
                              historyHref={`/offers/${offer.id}/history`}
                              onPreview={() => handlePreviewOffer(offer.id)}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {pageItems.length > 0 && (
                      <ListTotalRow
                        columnWidths={DESKTOP_COLUMNS.map(([, width]) => width)}
                        amounts={{ 3: totalAmount }}
                        actionColumnWidth={TABLE_ACTION_COL_WIDTH}
                      />
                    )}
                  </div>
                </div>

                <ListPagination
                  label={t('offers.showing', {
                    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                    to: Math.min(page * pageSize, totalElements),
                    total: totalElements
                  })}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('offers.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((offer) => {
                const config = statusConfig[offer.status ?? OfferStatus.DRAFT];
                const isExpired = offer.status === OfferStatus.EXPIRED;

                return (
                  <Stack
                    key={offer.id}
                    gap={10}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-soft)',
                      backgroundColor: 'var(--color-bg-surface)'
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text fz={14.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                          {otherPartyName(offer, user?.orgName)}
                        </Text>
                        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                          {offer.offerNumber ?? offer.referenceId}
                          {offer.objectName && (
                            <>
                              {' · '}
                              <Text span>{offer.objectName}</Text>
                            </>
                          )}
                        </Text>
                      </Stack>
                      <RowActionsMenu
                        detailsHref={`/offers/${offer.id}`}
                        historyHref={`/offers/${offer.id}/history`}
                        onPreview={() => handlePreviewOffer(offer.id)}
                      />
                    </Group>

                    <Text
                      fz={16}
                      fw={700}
                      style={{
                        color: isExpired ? 'var(--color-text-muted)' : 'var(--color-text-primary)'
                      }}
                    >
                      {formatAmount(offer.totalValue)}
                    </Text>

                    <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                      {t('offers.datePrefix')} {formatDate(offer.createdAt)} |{' '}
                      {t('offers.validUntilPrefix')} {formatDate(offer.validUntil)}
                    </Text>

                    <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                  </Stack>
                );
              })}

              <ListTotalCard amount={totalAmount} />

              <ListPagination
                label={t('offers.showing', {
                  from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, totalElements),
                  total: totalElements
                })}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
